/**
 * A refresh must drop the rows the server no longer has.
 *
 * The library ServiceStore MERGES a find into its cache: every row the
 * response carries is upserted, and every cached row it does NOT carry is
 * KEPT (`applyFindData` without `replaceOnFind`). That is safe for a store
 * many screens fill with different queries, and it is exactly why a row
 * another session removed stays on screen: when this tab missed the `removed`
 * event (it was asleep, its socket was reconnecting, the API was being
 * redeployed, or the service was not published at all), no find ever takes
 * the row away again — not the header Refresh, not the reconnect's re-enter.
 * Only a full page reload did (#2654, White Board 2026-09-22: "canceled /
 * deleted records keep appearing after removal").
 *
 * `findAndEvict` runs the caller's find and then removes, from that one
 * store, the cached rows that were IN THE FIND'S SCOPE before it was issued
 * and that the complete answer no longer contains. It is the view store that
 * knows the scope (the board asks for every lane; Client 360 asks for one
 * client's), so the eviction is scoped to it and never touches rows another
 * screen's query put there.
 *
 * Three rules keep it from ever removing a row that is still real:
 *
 *  1. ONLY A COMPLETE ANSWER EVICTS. A response the server truncated (its
 *     `total` is larger than the rows it sent, or a bare array that came back
 *     at the `$limit` asked for) says nothing about the rows past the cut, so
 *     nothing is evicted. A response with no readable rows (a find superseded
 *     by a newer one resolves `null`) evicts nothing either.
 *  2. A ROW AN EVENT TOUCHED DURING THE FIND IS FRESHER THAN THE ANSWER. The
 *     response was computed at some instant; a `created` / `patched` event can
 *     reach this tab before the response does. A row created after the
 *     snapshot was never in it, so it is never a candidate; a candidate whose
 *     cached copy changed while the find was in flight is left alone.
 *  3. EVICTION GOES THROUGH THE STORE'S OWN `removeEvent`, the same path a
 *     `removed` event takes, so the row leaves memory and the store's
 *     persisted cache together.
 */

type Row = Record<string, unknown>;

/** The slice of a library ServiceStore this needs. */
export interface EvictableStore {
  entities: unknown[];
  idField?: string;
  removeEvent?: (item: Row) => unknown;
}

export interface FindAndEvictOptions {
  /**
   * The `$limit` the find asked for. A bare-array answer holding this many
   * rows may have been cut short, so it evicts nothing.
   */
  limit?: number;
}

const identityOf = (row: Row, idField: string): string | null => {
  const id = row[idField] ?? row.id;
  return id === undefined || id === null ? null : String(id);
};

// shortcut: a row's "version" is its JSON. Exact and needs no per-service
// knowledge, but it serialises every in-scope row twice per find; fine for the
// few thousand rows a board holds, switch to `updatedAt` past ~10k rows.
const versionOf = (row: Row): string => JSON.stringify(row);

/** The rows of a find response, or null when it carries none we can trust. */
function rowsOf(response: unknown): Row[] | null {
  if (Array.isArray(response)) return response as Row[];
  const data = (response as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(data) ? (data as Row[]) : null;
}

/**
 * True when `response` is the WHOLE answer to the query, not a page of it.
 *
 * A `total` is trusted only beside a numeric `limit`, i.e. on the envelope a
 * paginated Feathers service builds. The library wraps a bare-array answer as
 * `{ data, total: data.length }`, a "total" that can never disagree with the
 * rows, so that shape falls back to "did it come back short of the limit".
 */
function isComplete(response: unknown, rows: Row[], limit: number | undefined): boolean {
  const shortOfLimit = limit === undefined || rows.length < limit;
  if (Array.isArray(response)) return shortOfLimit;
  const envelope = response as { total?: unknown; limit?: unknown; skip?: unknown };
  if (typeof envelope.total !== "number" || typeof envelope.limit !== "number") return shortOfLimit;
  const skip = typeof envelope.skip === "number" ? envelope.skip : 0;
  return skip === 0 && envelope.total <= rows.length;
}

/**
 * Run `find`, then evict from `store` every cached row `inScope` accepted
 * before the find was issued that the complete answer no longer holds.
 * Resolves with whatever `find` resolved; a rejection propagates and evicts
 * nothing.
 */
export async function findAndEvict<R>(
  store: EvictableStore | null | undefined,
  inScope: (row: Row) => boolean,
  find: () => Promise<R>,
  options: FindAndEvictOptions = {},
): Promise<R> {
  const idField = store?.idField || "id";
  const before = new Map<string, string>();
  for (const row of (store?.entities ?? []) as Row[]) {
    if (!row || !inScope(row)) continue;
    const id = identityOf(row, idField);
    if (id !== null) before.set(id, versionOf(row));
  }

  const response = await find();

  if (!store?.removeEvent || before.size === 0) return response;
  const rows = rowsOf(response);
  if (rows === null || !isComplete(response, rows, options.limit)) return response;

  const answered = new Set<string>();
  for (const row of rows) {
    const id = row ? identityOf(row, idField) : null;
    if (id !== null) answered.add(id);
  }
  const cachedNow = new Map<string, string>();
  for (const row of (store.entities ?? []) as Row[]) {
    const id = row ? identityOf(row, idField) : null;
    if (id !== null) cachedNow.set(id, versionOf(row));
  }
  for (const [id, version] of before) {
    if (answered.has(id)) continue;
    const now = cachedNow.get(id);
    // Gone already (a `removed` event beat the response), or changed by an
    // event while the find was in flight: either way not ours to remove.
    if (now === undefined || now !== version) continue;
    void Promise.resolve(store.removeEvent({ [idField]: id })).catch((err: unknown) => {
      console.error(`findAndEvict: could not drop ${id} from the cache`, err);
    });
  }
  return response;
}
